import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Wrench, Receipt, Clock, Warning, CheckCircle, Phone,
  CaretRight, Spinner, FileText, EnvelopeSimple, ShoppingCart, Buildings, Receipt as ReceiptIcon,
} from "@phosphor-icons/react";
import { fetchCompressorServiceForecast, fetchCompressorPMSchedule, fetchCompressorPhoneLogs, fetchCustomerPartsPurchased } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { extractErr } from "@/lib/errors";
import { useCart } from "@/lib/cart";
import EmailRecipientPicker from "@/components/EmailRecipientPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return String(d); }
}
function fmtMoney(v, ccy = "USD") {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return "—";
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n); }
  catch { return n.toFixed(2); }
}

const BUCKET_TONE = {
  due_now:      { label: "DUE NOW",      cls: "bg-red-50 text-red-700 border-red-300" },
  next_service: { label: "NEXT SERVICE", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  future:       { label: "UPCOMING",     cls: "bg-blue-50 text-blue-700 border-blue-200" },
  complete:     { label: "DONE",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

/**
 * CompressorDetailModal
 * ---------------------
 * Side-panel that opens when a compressor row is clicked anywhere in the
 * app. Surfaces:
 *  • Header card — SN, product type, owner, current run-hours, hours-until-next-service.
 *  • PM forecast — every Service_Table_by_SN row for this serial, bucketed
 *    into Due Now / Next Service / Upcoming / Done. Each row shows the
 *    qty-per-service, units already purchased, units short, the next
 *    threshold (hours), and a deep-link to the latest invoice that
 *    fulfilled it.
 *  • Parts purchased history filtered by Related-to-SN.
 *  • Phone-logs ascending, so the conversation history reads top-down.
 *
 * Props:
 *   serialId         — Products_By_Serial.Serial_numberID (8-char id)
 *   onClose          — close handler
 *   isCA             — true when the customer's brand is CA → red tint, else blue
 *   user             — current user (passed through to QuoteDetailModal)
 */
export default function CompressorDetailModal({ serialId, customerId, onClose, onOpenInvoice, isCA, user }) {
  const [forecast, setForecast] = useState(null);
  const [pm, setPm] = useState([]);
  const [parts, setParts] = useState([]);
  const [phoneLogs, setPhoneLogs] = useState([]);
  const [commissionings, setCommissionings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canTransfer, setCanTransfer] = useState(false);
  const [transferring, setTransferring] = useState(false);

  // Probe the user's permission once per modal open. Admins + Service Manager
  // role get the "Transfer ownership" button; everyone else doesn't even see it.
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/me/can-transfer-compressor`, {
      withCredentials: true,
      headers: user?.email ? { "X-User-Email": user.email } : {},
    }).then((r) => mounted && setCanTransfer(!!r.data?.can))
      .catch(() => mounted && setCanTransfer(false));
    return () => { mounted = false; };
  }, [user?.email]);

  const transferOwnership = async () => {
    const newOwner = window.prompt("New owner CustomerID (8-char hex):");
    if (!newOwner || !newOwner.trim()) return;
    if (!window.confirm(`Transfer SN ${forecast?.compressor?.serial_number || serialId} to ${newOwner.trim()}? The previous owner's "Compressor Owner" flag stays set per policy.`)) return;
    setTransferring(true);
    try {
      const res = await axios.post(`${API}/compressors/${encodeURIComponent(serialId)}/transfer`,
        { new_owner_customer_id: newOwner.trim(), note: "Transferred via UI" },
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
      );
      toast.success(`Transferred. Old owner: ${res.data.old_owner || "—"} → New owner: ${res.data.new_owner}`);
      onClose && onClose();
    } catch (e) {
      toast.error(extractErr(e, "Transfer failed").message);
    } finally {
      setTransferring(false);
    }
  };

  const load = useCallback(async () => {
    if (!serialId) return;
    setLoading(true); setError("");
    try {
      const [fc, pms, pl] = await Promise.all([
        fetchCompressorServiceForecast(serialId).catch(() => null),
        fetchCompressorPMSchedule(serialId).catch(() => ({ items: [] })),
        fetchCompressorPhoneLogs(serialId).catch(() => ({ items: [] })),
      ]);
      setForecast(fc);
      setPm(pms?.items || []);
      setPhoneLogs(pl?.items || []);
      // Past commissionings (sequence detection + history). Non-fatal.
      try {
        const cm = await axios.get(`${API}/commissioning/by-serial/${encodeURIComponent(serialId)}`, {
          withCredentials: true,
          headers: user?.email ? { "X-User-Email": user.email } : {},
        });
        setCommissionings((cm.data?.items || []).filter(c => c.status === "submitted"));
      } catch { setCommissionings([]); }
      // Parts purchased filtered by serial — uses the customer-scoped endpoint.
      const ownerCustomerId = customerId || fc?.compressor?.owner_id;
      if (ownerCustomerId) {
        try {
          const pp = await fetchCustomerPartsPurchased(ownerCustomerId, {
            serial: fc?.compressor?.serial_number || "", limit: 200,
          });
          setParts(pp.items || []);
        } catch { setParts([]); }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally { setLoading(false); }
  }, [serialId, customerId]);

  useEffect(() => { load(); }, [load]);

  const c = forecast?.compressor || {};
  const summary = forecast?.summary || {};
  const items = forecast?.items || [];

  // Bucket the items so the table reads naturally
  const dueNow = items.filter((i) => i.bucket === "due_now");
  const nextSvc = items.filter((i) =>
    i.bucket === "future" && i.next_service_at_hour && i.next_service_at_hour === summary.next_service_at_hour
  );
  const upcoming = items.filter((i) =>
    i.bucket === "future" && (!summary.next_service_at_hour || i.next_service_at_hour !== summary.next_service_at_hour)
  );
  const done = items.filter((i) => i.bucket === "complete");

  // req #3 (iter111): every row whose forecast says we're SHORT — even
  // long-overdue. Surfaced as a dedicated red section below the bucket
  // tables so dealers can act on it without hunting through "due now".
  const missing = useMemo(
    () => items.filter((i) => (i.units_short || 0) > 0),
    [items],
  );

  // req #7 (iter111): selection state for "Add to cart" + "Email this list".
  const [selectedMissing, setSelectedMissing] = useState(new Set());
  useEffect(() => {
    // When the underlying list changes (load / refresh), default to
    // "all missing rows selected" — matches the spec ("total missing
    // parts OR user selects which").
    setSelectedMissing(new Set(missing.map((m) => String(m.id))));
  }, [missing]);
  const toggleMissing = (id) => {
    const k = String(id);
    setSelectedMissing((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const allMissingSelected = missing.length > 0
    && missing.every((m) => selectedMissing.has(String(m.id)));

  // Email modals (req #5 + #6 + iter113 quote)
  const [emailMode, setEmailMode] = useState(null); // 'pm_schedule' | 'missing_parts' | 'create_quote'
  const [emailBusy, setEmailBusy] = useState(false);
  const ownerCustomerId = c.owner_id || customerId || "";

  const navigate = useNavigate();
  const { addItem } = useCart();

  const tint = isCA ? "border-red-100 bg-red-50/30" : "border-blue-100 bg-blue-50/30";

  // ─── Action handlers (req #5, #6, #7) ────────────────────────────
  const addMissingToCart = () => {
    const targets = missing.filter((m) => selectedMissing.has(String(m.id)));
    if (!targets.length) {
      toast.error("Pick at least one part first");
      return;
    }
    let added = 0;
    let skipped = 0;
    for (const m of targets) {
      // Backend returns "" when the Parts row has no SKU; can't add
      // those because cart keys on `product_code`. Tell the user.
      if (!m.product_code) { skipped += 1; continue; }
      const qty = Math.max(1, Math.ceil(Number(m.units_short) || 1));
      addItem({
        product_code: m.product_code,
        product_name: m.part_name,
        price: Number(m.unit_price) || 0,
        currency: isCA ? "CAD" : "USD",
        category: m.part_class || "PM Part",
      }, qty);
      added += 1;
    }
    if (added && skipped) {
      toast.success(`Added ${added} to cart — ${skipped} skipped (no SKU on file)`);
    } else if (added) {
      toast.success(`Added ${added} item(s) to cart`);
    } else {
      toast.error("None of the selected parts have a SKU on file");
    }
  };

  const sendEmail = async ({ recipients, message, include_prices }) => {
    setEmailBusy(true);
    try {
      let path;
      if (emailMode === "missing_parts") path = "email-missing-parts";
      else if (emailMode === "create_quote") path = "missing-parts/quote";
      else path = "email-pm-schedule";
      const body = { recipients, message, include_prices };
      if (emailMode === "missing_parts" || emailMode === "create_quote") {
        // If user UN-checked some rows, send only the checked ones.
        const ids = Array.from(selectedMissing);
        if (ids.length && ids.length !== missing.length) body.item_ids = ids;
      }
      const r = await axios.post(
        `${API}/compressors/${encodeURIComponent(serialId)}/${path}`,
        body,
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} },
      );
      const { delivered, throttled, failed, quote_id, share_url } = r.data || {};
      if (emailMode === "create_quote" && quote_id) {
        // Quote was created even if email send failed — toast both pieces.
        if (delivered > 0) {
          toast.success(`Quote ${quote_id} created · emailed to ${delivered} recipient(s)`);
        } else {
          toast.warning(`Quote ${quote_id} created — email send failed (${failed || 0} fail, ${throttled || 0} throttled). Share URL copied.`);
        }
        // Also copy the magic-link to clipboard so the dealer can paste it.
        if (share_url && navigator.clipboard) {
          try { await navigator.clipboard.writeText(share_url); } catch { /* clipboard blocked — non-fatal */ }
        }
      } else if (delivered > 0 && !throttled && !failed) {
        toast.success(`Email sent to ${delivered} recipient(s)`);
      } else if (delivered > 0) {
        toast.warning(`Sent ${delivered} · throttled ${throttled || 0} · failed ${failed || 0}`);
      } else {
        toast.error("Email send failed — see /admin/email-limits");
      }
      setEmailMode(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="compressor-detail-modal">
      <div className="bg-white rounded-md w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className={`flex items-start justify-between gap-3 p-4 border-b ${isCA ? "border-red-100" : "border-blue-100"}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Wrench size={16} className={isCA ? "text-red-600" : "text-blue-700"} />
              <h2 className="text-lg font-black tracking-tight font-['Chivo'] truncate">
                {c.product_name || c.product_type || "Compressor"}
              </h2>
              <span className="text-xs font-mono text-slate-500">SN {c.serial_number || serialId}</span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500 flex-wrap">
              {c.product_type && <span>{c.product_type}</span>}
              {c.date_delivered && <span>· delivered {fmtDate(c.date_delivered)}</span>}
              {c.owner_company_name && (
                <button
                  type="button"
                  onClick={() => {
                    if (!ownerCustomerId) return;
                    onClose && onClose();
                    // Deep-link: CustomerPage reads ?focus=<id> and
                    // auto-opens the detail panel.
                    const path = isCA ? "/customers-ca" : "/customers-us";
                    navigate(`${path}?focus=${encodeURIComponent(ownerCustomerId)}`);
                  }}
                  data-testid="compressor-owner-link"
                  className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline font-medium"
                  title="Open company detail"
                >
                  <Buildings size={11} weight="bold" />
                  {c.owner_company_name}
                </button>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="compressor-detail-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        {canTransfer && (
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-amber-50/60">
            <span className="text-[11px] text-amber-800">
              You have permission to transfer ownership of this compressor.
            </span>
            <button
              type="button"
              onClick={transferOwnership}
              disabled={transferring}
              data-testid="compressor-transfer-btn"
              className="text-[10px] font-bold uppercase tracking-wider px-3 h-7 rounded-sm border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            >
              {transferring ? "Transferring…" : "Transfer ownership"}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-12 text-slate-500">
            <Spinner size={20} className="animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700 bg-red-50" data-testid="compressor-detail-error">
            <Warning size={14} className="inline -mt-0.5 mr-1" /> {error}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {/* Summary strip — req #2 (iter110): "Units short" is meaningless
                 (the gross delta isn't actionable on its own); replaced by
                 "Last connection" so the user immediately knows whether the
                 unit's hours are recent. The per-row missing parts list is
                 surfaced in the upcoming "Missing parts" section instead. */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="compressor-summary">
              <SummaryCard label="Run hours" value={summary.current_hours != null ? Number(summary.current_hours).toLocaleString() : "—"} icon={Clock} tone={tint} />
              <SummaryCard label="Next service at" value={summary.next_service_at_hour != null ? `${Number(summary.next_service_at_hour).toLocaleString()} h` : "—"} icon={CheckCircle} tone={tint} />
              <SummaryCard label="Hours until" value={summary.hours_until_next != null ? Number(summary.hours_until_next).toLocaleString() : "—"} icon={Clock} tone={tint} accent={summary.hours_until_next != null && summary.hours_until_next < 100 ? "warn" : null} />
              <SummaryCard label="Last connection" value={fmtDate(c.last_connection)} icon={Phone} tone={tint} />
            </section>

            {/* PM forecast — Due Now */}
            {dueNow.length > 0 && (
              <ForecastBucket title="Due Now" items={dueNow} tone="red" tint={tint} onOpenInvoice={onOpenInvoice} />
            )}

            {/* Next service (the upcoming one we MUST plan for now) */}
            {nextSvc.length > 0 && (
              <ForecastBucket title={`Next service @ ${summary.next_service_at_hour?.toLocaleString()} h`} items={nextSvc} tone="amber" tint={tint} onOpenInvoice={onOpenInvoice} />
            )}

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <ForecastBucket title="Upcoming services" items={upcoming} tone="blue" tint={tint} onOpenInvoice={onOpenInvoice} collapsibleAfter={4} />
            )}

            {/* Done */}
            {done.length > 0 && (
              <ForecastBucket title="Completed (one-time)" items={done} tone="emerald" tint={tint} onOpenInvoice={onOpenInvoice} collapsibleAfter={4} />
            )}

            {/* Action toolbar — req #5: email the full PM schedule. */}
            <div className="flex items-center justify-between gap-2 px-1" data-testid="compressor-action-toolbar">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">PM &amp; orders</span>
              <button
                type="button"
                onClick={() => setEmailMode("pm_schedule")}
                data-testid="compressor-email-pm-btn"
                className="text-[11px] font-semibold uppercase tracking-wider px-3 h-8 rounded-sm border border-slate-200 hover:border-blue-400 hover:text-blue-700 inline-flex items-center gap-1.5">
                <EnvelopeSimple size={12} weight="bold" /> Email PM schedule
              </button>
            </div>

            {/* req #3 + #6 + #7: Missing parts (red panel). Always shown
                when there's at least one short row, regardless of how
                long-overdue. Includes per-row checkboxes feeding the
                "Add to cart" + "Email this list" actions. */}
            {missing.length > 0 && (
              <section data-testid="compressor-missing-section" className="rounded-md border border-red-200 bg-red-50/40 overflow-hidden">
                <header className="flex items-center gap-3 px-3 py-2 border-b border-red-200 bg-red-100/50">
                  <Warning size={14} weight="fill" className="text-red-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[11px] font-black tracking-tight uppercase text-red-900 font-['Chivo']">
                      Missing parts ({missing.length})
                    </h4>
                    <p className="text-[10px] text-red-800/80">
                      These services are overdue or short on stock. Add to cart, or email the dealer the shopping list.
                    </p>
                  </div>
                  <button type="button" onClick={() => {
                      setSelectedMissing(allMissingSelected ? new Set() : new Set(missing.map((m) => String(m.id))));
                    }}
                    data-testid="compressor-missing-toggle-all"
                    className="text-[10px] font-semibold text-red-800 hover:underline shrink-0">
                    {allMissingSelected ? "Deselect all" : "Select all"}
                  </button>
                </header>
                <ul className="divide-y divide-red-100">
                  {missing.map((m) => {
                    const id = String(m.id);
                    const checked = selectedMissing.has(id);
                    return (
                      <li key={id}
                        className={`flex items-start gap-2 px-3 py-2 text-xs cursor-pointer ${checked ? "bg-white/60" : ""}`}
                        onClick={() => toggleMissing(id)}
                        data-testid={`compressor-missing-row-${id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleMissing(id)}
                          className="accent-red-600 cursor-pointer mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {m.service_type && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-red-100 text-red-800 border border-red-200">{m.service_type}</span>
                            )}
                            <span className="font-medium text-slate-800 truncate">{m.part_name}</span>
                            {!m.product_code && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-200" title="No SKU on file — can't add to cart">No SKU</span>
                            )}
                          </div>
                          <div className="text-[10px] text-red-800/80 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold">Short {m.units_short} {m.unit_hint}</span>
                            {m.unit_price != null && <span>· {fmtMoney(m.unit_price, isCA ? "CAD" : "USD")}/{m.unit_hint}</span>}
                            {m.next_service_at_hour > 0 && <span>· next @ {m.next_service_at_hour.toLocaleString()} h</span>}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex items-center justify-end gap-2 px-3 py-2 bg-red-100/30 border-t border-red-200 flex-wrap">
                  <button type="button" onClick={addMissingToCart}
                    data-testid="compressor-missing-add-cart"
                    disabled={selectedMissing.size === 0}
                    className="text-[11px] font-semibold uppercase tracking-wider px-3 h-8 rounded-sm border border-red-300 text-red-800 bg-white hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                    <ShoppingCart size={12} weight="bold" /> Add to cart ({selectedMissing.size})
                  </button>
                  <button type="button" onClick={() => setEmailMode("missing_parts")}
                    data-testid="compressor-missing-email-btn"
                    disabled={selectedMissing.size === 0}
                    className="text-[11px] font-semibold uppercase tracking-wider px-3 h-8 rounded-sm border border-red-300 text-red-800 bg-white hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                    <EnvelopeSimple size={12} weight="bold" /> Email this list
                  </button>
                  {/* iter113 — create real Quote + email magic-link "buy now"
                      CTA. Skips rows with no SKU on file (the backend
                      filters them out and 400s if none qualify). */}
                  <button type="button" onClick={() => setEmailMode("create_quote")}
                    data-testid="compressor-missing-quote-btn"
                    disabled={selectedMissing.size === 0}
                    className="text-[11px] font-bold uppercase tracking-wider px-3 h-8 rounded-sm bg-red-700 text-white hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                    <ReceiptIcon size={12} weight="bold" /> Create quote &amp; email
                  </button>
                </div>
              </section>
            )}

            {/* Parts purchased history for this SN */}
            {parts.length > 0 && (
              <section data-testid="compressor-parts-section">
                <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1.5">
                  <Receipt size={11} weight="bold" /> Parts purchased for this compressor <span className="text-slate-500 normal-case tracking-normal">({parts.length})</span>
                </h4>
                <div className={`rounded-md border ${tint} overflow-hidden`}>
                  <table className="w-full text-xs">
                    <thead className={`text-[10px] font-bold uppercase tracking-wider text-slate-500 ${isCA ? "bg-red-50/60" : "bg-blue-50/60"}`}>
                      <tr>
                        <th className="px-2 py-1.5 text-left">Part</th>
                        <th className="px-2 py-1.5 text-right w-16">Qty</th>
                        <th className="px-2 py-1.5 text-right w-24">Price</th>
                        <th className="px-2 py-1.5 w-28">Invoice</th>
                        <th className="px-2 py-1.5 w-28">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parts.map((p, idx) => (
                        <tr
                          key={p.ID}
                          data-testid={`compressor-part-row-${p.ID}`}
                          onClick={() => p.heading_id && onOpenInvoice && onOpenInvoice(p.heading_id)}
                          className={`${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} ${p.heading_id ? "hover:bg-slate-50 cursor-pointer" : ""}`}
                        >
                          <td className="px-2 py-1.5 text-slate-700">
                            {p.product_name || p.product_code}
                            {p.product_class && <span className="text-[10px] text-slate-400 ml-1">· {p.product_class}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">{Number(p.quantity || 0).toLocaleString()}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(p.price)}</td>
                          <td className="px-2 py-1.5 text-blue-700 font-mono">{p.invoice_number ? `#${p.invoice_number}` : "—"}</td>
                          <td className="px-2 py-1.5 text-slate-500">{fmtDate(p.invoice_date || p.quote_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Phone log history */}
            {phoneLogs.length > 0 && (
              <section data-testid="compressor-phone-logs-section">
                <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1.5">
                  <Phone size={11} weight="bold" /> Call history <span className="text-slate-500 normal-case tracking-normal">({phoneLogs.length})</span>
                </h4>
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {phoneLogs.map((l, idx) => (
                    <div
                      key={l.id}
                      data-testid={`compressor-phone-row-${l.id}`}
                      className={`rounded-sm border p-2 text-xs ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/40" : "bg-blue-50/40")} ${isCA ? "border-red-100" : "border-blue-100"}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] text-slate-500">{fmtDate(l.call_at)}</span>
                        {l.call_type && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">{l.call_type}</span>}
                        {l.direction && <span className="text-[9px] text-slate-500">· {l.direction}</span>}
                        {l.duration && <span className="text-[9px] text-slate-500">· {l.duration}s</span>}
                        {l.handled_by && (
                          <span className="text-[9px] text-slate-500 ml-auto inline-flex items-center gap-1" data-testid={`compressor-phone-handler-${l.id}`}>
                            handled by <strong className="text-slate-700">{l.handled_by_name || l.handled_by}</strong>
                            {l.handled_by_position && (
                              <span className="text-[9px] uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 px-1 rounded-sm" title={l.handled_by_dept || ""}>{l.handled_by_position}</span>
                            )}
                          </span>
                        )}
                      </div>
                      {l.summary && <p className="text-slate-700 line-clamp-2">{l.summary}</p>}
                      {!l.summary && l.transcript && <p className="text-slate-700 line-clamp-2 italic">{l.transcript}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Past commissionings (warranty / install history) */}
            {commissionings.length > 0 && (
              <section data-testid="compressor-commissionings">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 px-3 pt-3">
                  Commissioning history ({commissionings.length})
                </h3>
                <div className="px-3 pb-3 mt-2 space-y-2">
                  {commissionings.map(c => (
                    <div key={c.id} data-testid={`compressor-commissioning-${c.id}`}
                      className="border rounded p-2.5 bg-emerald-50">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="bg-emerald-600 text-white px-2 py-0.5 rounded font-bold text-[10px]">#{c.sequence_number}</span>
                          <span className="font-bold">{c.finished_at ? new Date(c.finished_at).toLocaleDateString() : "draft"}</span>
                          <span className="text-xs text-slate-600">by {c.tech_email}</span>
                        </div>
                        <div className="flex gap-2">
                          {c.disclaimer?.signed_pdf_filename && (
                            <a href={`${API}/commissioning/${c.id}/disclaimer.pdf`}
                              target="_blank" rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 font-bold">PDF →</a>
                          )}
                        </div>
                      </div>
                      {c.measurements?.voltage && (
                        <div className="text-xs text-slate-700 mt-1">
                          {[
                            c.measurements.voltage.l1_l2 != null ? `L1↔L2 ${c.measurements.voltage.l1_l2}V` : "",
                            c.measurements.voltage.l2_l3 != null ? `L2↔L3 ${c.measurements.voltage.l2_l3}V` : "",
                            c.measurements.voltage.l1_l3 != null ? `L1↔L3 ${c.measurements.voltage.l1_l3}V` : "",
                          ].filter(Boolean).join(" · ")}
                          {c.measurements.voltage_imbalance_pct != null && (
                            <span className={c.measurements.voltage_imbalance_flag ? "text-red-700 font-bold ml-2" : "text-slate-500 ml-2"}>
                              ({c.measurements.voltage_imbalance_pct}% imbalance)
                            </span>
                          )}
                        </div>
                      )}
                      {c.measurements?.wire_diameter_compressor && (
                        <div className="text-xs text-slate-600">Wire: {c.measurements.wire_diameter_compressor}{c.measurements.wire_diameter_dryer ? ` (compressor) · ${c.measurements.wire_diameter_dryer} (dryer)` : ""}</div>
                      )}
                      {c.sequence_reason && (
                        <div className="text-xs italic text-slate-600 mt-1">"{c.sequence_reason}"</div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Empty-state hint when there's literally nothing */}
            {pm.length === 0 && parts.length === 0 && phoneLogs.length === 0 && commissionings.length === 0 && (
              <div className="p-12 text-center text-sm text-slate-400" data-testid="compressor-empty">
                No PM schedule, parts, or call history recorded for this compressor yet.
              </div>
            )}
          </div>
        )}
        {/* Invoice deep-link bubbles up to the CustomerDetail parent which already
            owns its own <QuoteDetailModal>; we don't render it here to avoid the
            cyclic import. */}

        {/* req #5 + #6 + iter113 — recipient picker shared by all 3 email actions. */}
        {emailMode && (
          <EmailRecipientPicker
            open={!!emailMode}
            onClose={() => { if (!emailBusy) setEmailMode(null); }}
            onSubmit={sendEmail}
            user={user}
            ownerCustomerId={ownerCustomerId}
            title={
              emailMode === "missing_parts" ? "Email Missing Parts List"
              : emailMode === "create_quote" ? "Create Quote & Email"
              : "Email PM Schedule"
            }
            subtitle={
              emailMode === "missing_parts"
                ? `Send the ${selectedMissing.size} selected missing item(s) to your dealer or fleet manager.`
                : emailMode === "create_quote"
                  ? `Generate a real Preliminary quote with ${selectedMissing.size} item(s) and email a one-click "buy now" link to the recipient(s). The quote is editable from your sales-team console after creation.`
                  : "Send the maintenance plan to your dealer or fleet manager."
            }
            busy={emailBusy}
            testIdPrefix={
              emailMode === "missing_parts" ? "email-missing"
              : emailMode === "create_quote" ? "email-quote"
              : "email-pm"
            }
          />
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone, accent }) {
  const accentCls = accent === "warn" ? "text-red-600 font-bold" : "text-slate-800";
  return (
    <div className={`rounded-md border p-2.5 ${tone}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {Icon && <Icon size={11} weight="bold" />}
        {label}
      </div>
      <div className={`text-xl font-black mt-1 leading-tight font-['Chivo'] ${accentCls}`}>{value}</div>
    </div>
  );
}

function ForecastBucket({ title, items, tone, tint, onOpenInvoice, collapsibleAfter }) {
  const [expanded, setExpanded] = useState(!collapsibleAfter || items.length <= collapsibleAfter);
  const display = expanded ? items : items.slice(0, collapsibleAfter || items.length);
  const titleTone = {
    red: "text-red-700",
    amber: "text-amber-700",
    blue: "text-blue-700",
    emerald: "text-emerald-700",
  }[tone] || "text-slate-700";
  return (
    <section>
      <h4 className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5 ${titleTone}`}>
        {title} <span className="text-slate-500 normal-case tracking-normal">({items.length})</span>
      </h4>
      <div className={`rounded-md border ${tint} overflow-hidden`}>
        <div className="divide-y divide-slate-100">
          {display.map((it) => (
            <ForecastRow key={it.id} item={it} onOpenInvoice={onOpenInvoice} />
          ))}
        </div>
      </div>
      {collapsibleAfter && items.length > collapsibleAfter && (
        <button
          onClick={() => setExpanded((x) => !x)}
          className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800"
        >
          {expanded ? "Show less" : `Show ${items.length - collapsibleAfter} more`}
        </button>
      )}
    </section>
  );
}

function ForecastRow({ item, onOpenInvoice }) {
  const [expanded, setExpanded] = useState(false);
  const pill = BUCKET_TONE[item.bucket] || BUCKET_TONE.future;
  // req #2 (iter110): "units short" hidden as a per-row stat — it's
  // covered by the dedicated Missing-Parts section below the buckets.
  const purchasedClass = (item.units_purchased || 0) > 0 ? "text-emerald-700" : "text-slate-400";
  return (
    <div data-testid={`forecast-row-${item.id}`} className="text-xs">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full text-left p-2 flex items-start gap-2 hover:bg-slate-50/80"
      >
        <div className="shrink-0 pt-0.5">
          <CaretRight size={11} className={`text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${pill.cls}`}>{pill.label}</span>
            {item.service_type && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">{item.service_type}</span>}
            <span className="font-medium text-slate-800 truncate">{item.part_name}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500 flex-wrap">
            {item.interval_hours > 0 && <span>every {item.interval_hours.toLocaleString()} h</span>}
            {item.next_service_at_hour > 0 && <span>· next @ {item.next_service_at_hour.toLocaleString()} h</span>}
            {item.hours_until != null && <span>· in {item.hours_until.toLocaleString()} h</span>}
            <span>· need <span className="font-mono">{item.qty_per_service} {item.unit_hint}</span></span>
            <span className={purchasedClass}>· bought {item.units_purchased}</span>
          </div>
        </div>
        {item.last_invoice_number && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenInvoice && onOpenInvoice(item.last_invoice_id || item.last_invoice_number); }}
            className="shrink-0 text-[10px] text-blue-700 hover:underline whitespace-nowrap"
            title={`Last bought on ${item.last_invoice_date || ""}`}
          >
            INV #{item.last_invoice_number}
          </button>
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-2 pl-6">
          {item.description ? (
            <p className="text-[11px] text-slate-700 whitespace-pre-line bg-slate-50/80 p-2 rounded-sm border border-slate-200">
              <FileText size={10} weight="bold" className="inline -mt-0.5 mr-1 text-slate-400" />
              {item.description}
            </p>
          ) : (
            <p className="text-[10px] text-slate-400 italic">No technician notes recorded for this PM step.</p>
          )}
        </div>
      )}
    </div>
  );
}
